/**
 * Lets any descendant of a page layout inject content into the right
 * side of that layout's page heading row.
 *
 * Used so the layout can own the H1 + subtitle (consistent typography
 * across every page) while still allowing the data-fetching view
 * inside to push its dynamic context (date range, scope, etc.) into
 * the same row — instead of rendering a second pseudo-header below.
 *
 * Pattern:
 *   <PageHeadingProvider value={{ setActions }}>
 *     <PageHeading actions={actions} ... />
 *     <Outlet />
 *   </PageHeadingProvider>
 *
 * Inside the outlet's data-fetching component:
 *   const { setActions } = usePageHeading();
 *   useEffect(() => {
 *     setActions(<MyChipAndScopeRow />);
 *     return () => setActions(null);
 *   }, [data]);
 */

import { createContext, useContext, type ReactNode } from 'react';

interface PageHeadingApi {
  setActions: (node: ReactNode) => void;
}

const PageHeadingContext = createContext<PageHeadingApi | null>(null);

export const PageHeadingProvider = PageHeadingContext.Provider;

// eslint-disable-next-line react-refresh/only-export-components -- co-located with the provider for ergonomics
export function usePageHeading(): PageHeadingApi {
  const ctx = useContext(PageHeadingContext);
  // Returning a no-op when no provider is mounted means a child
  // component can call setActions unconditionally without crashing on
  // pages that don't host the heading slot.
  return ctx ?? { setActions: () => {} };
}
